import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useSupabase } from "@/auth/SupabaseProvider";

import { apiFetch } from "./client";
import type {
  DecidedPresentationsResponse,
  PendingPresentationsResponse,
  PresentationReviewResponse,
  PresentationReviewSubmitBody,
  PresentationReviewSubmitResponse,
  RecentPresentationsResponse,
} from "./types";

/**
 * URL-shaped filter state for the past-reviews subtab (issue #606).
 * All fields optional; the hook translates them into the equivalent
 * `/api/presentations/decided` query params. `decision` carries a set
 * of enum values that gets serialised as a comma-separated list.
 */
export interface DecidedPresentationsFilters {
  /** Subset of verdict values to include. Undefined ⇒ all three. */
  decision?: readonly ("approved" | "rejected" | "expired")[];
  /** ISO date-only (`YYYY-MM-DD`) or full ISO datetime. */
  from?: string;
  /** ISO date-only (`YYYY-MM-DD`) or full ISO datetime. */
  to?: string;
  /** Server-side cap 200; undefined uses the backend default (50). */
  limit?: number;
}

/**
 * Hooks for the M2 admin HITL presentation-review surface (issue #144).
 * Sibling of `./review.ts` for the M1 curator-draft review — same
 * TanStack Query idiom, different endpoints.
 */

/**
 * Fetch a single presentation for the review surface. The response
 * carries the memo (every attacker-influenced field), the validator-
 * enriched recommendations jsonb, the validation score + status, the
 * Drive doc handle, and the decision metadata. No polling — the row
 * only mutates via `useSubmitPresentationReview`, which invalidates
 * this query on success.
 */
export function usePresentation(presentationId: string | undefined) {
  const { client } = useSupabase();
  return useQuery({
    queryKey: ["presentation", presentationId],
    enabled: Boolean(presentationId),
    queryFn: ({ signal }) =>
      apiFetch<PresentationReviewResponse>(
        client,
        `/api/presentations/${presentationId}`,
        { signal },
      ),
  });
}

/**
 * Fetch the cross-presentation pending-review queue. Used by the
 * Reviews tab to render pending presentations alongside the existing
 * pending curator-draft batches.
 */
export function usePendingPresentations() {
  const { client } = useSupabase();
  return useQuery({
    queryKey: ["pending-presentations"],
    queryFn: ({ signal }) =>
      apiFetch<PendingPresentationsResponse>(
        client,
        "/api/review/pending-presentations",
        { signal },
      ),
  });
}

/**
 * Fetch the nav-level Presentations queue — the pending queue plus
 * the last 50 decided rows (approved / rejected / expired / failed).
 * Powers the `/presentations` route (issue #575). Each row carries
 * the friendly client name + company + memo number so the list
 * doesn't expose UUIDs.
 */
export function useRecentPresentations() {
  const { client } = useSupabase();
  return useQuery({
    queryKey: ["recent-presentations"],
    queryFn: ({ signal }) =>
      apiFetch<RecentPresentationsResponse>(
        client,
        "/api/presentations/recent",
        { signal },
      ),
  });
}

/**
 * Fetch the past-reviews list (issue #606) — decided presentations
 * only (`approved` / `rejected` / `expired`), sorted by decision
 * timestamp DESC, capped at 200 rows. Powers the "Decided" subtab on
 * `/presentations`. Filters live in the URL search params so refresh
 * + back preserve the view.
 *
 * The query key includes the filter object so switching filters
 * refetches independently rather than sharing a stale cache entry.
 * The mutating actions (`useSubmitPresentationReview`) prefix-
 * invalidate `["decided-presentations"]` so an approve/reject
 * immediately reflects on this list.
 */
export function useDecidedPresentations(filters: DecidedPresentationsFilters) {
  const { client } = useSupabase();
  const query: Record<string, string | number | undefined> = {};
  if (filters.decision && filters.decision.length > 0) {
    query.decision = filters.decision.join(",");
  }
  if (filters.from) query.from = filters.from;
  if (filters.to) query.to = filters.to;
  if (filters.limit !== undefined) query.limit = filters.limit;

  return useQuery({
    queryKey: [
      "decided-presentations",
      {
        decision: filters.decision ? [...filters.decision].sort() : null,
        from: filters.from ?? null,
        to: filters.to ?? null,
        limit: filters.limit ?? null,
      },
    ],
    queryFn: ({ signal }) =>
      apiFetch<DecidedPresentationsResponse>(
        client,
        "/api/presentations/decided",
        { signal, query },
      ),
  });
}

/**
 * Submit the human verdict on a presentation. The orchestrator writes
 * `presentation_review_decision_recorded` + `presentation_review_completed`
 * audit rows, transitions `solution_presentations.status` to the verdict,
 * and returns 409 `already_decided` if the row is terminal or the
 * UPDATE lost the race.
 *
 * On success we prefix-invalidate `["presentation"]` so the active
 * `usePresentation` cache entry refetches regardless of which URL
 * identifier shape is keying it. The `:id` path segment is polymorphic
 * (issue #603 — accepts UUID or padded / unpadded `memo_number`) and
 * `PresentationReview` swaps between shapes via a canonicalising
 * `navigate(replace)`, so multiple key variants can coexist in the
 * cache within a single session. A key-exact invalidation targeted at
 * whichever shape the caller happens to pass would miss the currently
 * active variant and leave the SPA stuck on the pre-decision view
 * (issue #620). Also invalidates the pending queue (row drops), the
 * recent queue (row moves from pending → decided), and the past-
 * reviews queue (issue #606 — the fresh verdict belongs there too).
 */
export function useSubmitPresentationReview(presentationId: string | undefined) {
  const { client } = useSupabase();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PresentationReviewSubmitBody) =>
      apiFetch<PresentationReviewSubmitResponse>(
        client,
        `/api/presentations/${presentationId}/review`,
        { method: "POST", body },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["presentation"] });
      queryClient.invalidateQueries({ queryKey: ["pending-presentations"] });
      queryClient.invalidateQueries({ queryKey: ["recent-presentations"] });
      // Past-reviews subtab (issue #606) — a fresh decision belongs on
      // this list. Prefix-invalidation catches every filter-keyed
      // variant that might be cached.
      queryClient.invalidateQueries({ queryKey: ["decided-presentations"] });
    },
  });
}
